import React, { useState, useEffect } from "react";
import { channels } from "../shared/constants";
import {
	LayoutDashboard,
	MessageSquare,
	LogOut,
	ChevronUp,
	User,
	Settings,
	X,
	Info,
	EditIcon,
	TrashIcon,
	CheckCircleIcon,
	XCircleIcon,
	PlayCircle,
	Clock,
	Star,
	CheckCircle,
} from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../components/ui/dialog";
import { toast } from "react-toastify";
import { validateAuthToken, fetchOzapAiConfig } from "../api";
const { ipcRenderer, shell } = window.require("electron");

import Logo from "../img/logo.png";
import { Dashboard } from "../pages/Dashboard";
import { Chat } from "../pages/Chat";
import { Tutorial } from "../pages/Tutorial";
import { UpdatesDialog } from "../pages/Dashboard/components/UpdatesDialog";
import { LogsDialog } from "../pages/Dashboard/components/LogsDialog";
import { AIErrorNotification } from "../pages/Dashboard/components/AIErrorNotification";
import { APP_LOGO_ALT, APP_NAME, SUPPORT_WHATSAPP } from "../config";
import { Grid2X2 } from "lucide-react";
import { DashboardV2 } from "../pages/DashboardV2";
import { LayoutPanelTop } from "lucide-react";

const { version } = require("../../package.json");

/**
 * @typedef {Object} AppLayoutProps
 * @property {() => void} onLogout - Função para realizar logout
 */

/**
 * Componente principal de layout da aplicação que gerencia navegação e instâncias
 * @param {AppLayoutProps} props - Propriedades do componente
 * @returns {JSX.Element} Componente React renderizado
 */
function AppLayout({ onLogout }) {
	/**
	 * Calcula o tempo restante até o fim da promoção
	 * @returns {Object} Objeto com os tempos restantes
	 */
	const calculateTimeRemaining = () => {
		const promoEndDate = new Date('2025-10-14T23:59:59');
		const now = new Date();
		const difference = promoEndDate.getTime() - now.getTime();

		if (difference <= 0) {
			return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
		}

		const days = Math.floor(difference / (1000 * 60 * 60 * 24));
		const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
		const minutes = Math.floor((difference / 1000 / 60) % 60);
		const seconds = Math.floor((difference / 1000) % 60);

		return { days, hours, minutes, seconds, expired: false };
	};

	/** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
	const [currentPage, setCurrentPage] = useState("dashboard");

	/** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
	const [menuExpanded, setMenuExpanded] = useState(false);

	/** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
	const [instanceSelectionDialogOpen, setInstanceSelectionDialogOpen] =
		useState(false);

	/** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
	const [logsDialogOpen, setLogsDialogOpen] = useState(false);

	/** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
	const [updatesDialogOpen, setUpdatesDialogOpen] = useState(false);

	/** @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]} */
	const [options247DialogOpen, setOptions247DialogOpen] = useState(false);

	/** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
	const [instanceName, setInstanceName] = useState("Padrão");

	/** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
	const [accessUntil, setAccessUntil] = useState("Carregando...");

	/** @type {[string[], React.Dispatch<React.SetStateAction<string[]>>]} */
	const [existingInstances, setExistingInstances] = useState([]);

	/** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
	const [newInstanceName, setNewInstanceName] = useState("");

	/** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
	const [instanceId, setInstanceId] = useState("default");

	/** @type {[Object, React.Dispatch<React.SetStateAction<Object>>]} */
	const [instances, setInstances] = useState({});

	/** @type {[string|null, React.Dispatch<React.SetStateAction<string|null>>]} */
	const [isEditing, setIsEditing] = useState(null);

	/** @type {[string, React.Dispatch<React.SetStateAction<string>>]} */
	const [editedInstanceName, setEditedInstanceName] = useState("");

	/** @type {[string[], React.Dispatch<React.SetStateAction<string[]>>]} */
	const [logs, setLogs] = useState([]);

	/** @type {[number|null, React.Dispatch<React.SetStateAction<number|null>>]} */
	const [instancesLimit, setInstancesLimit] = useState(null);

	// Estado para controlar o banner de promoção
	const [showPromotionBanner, setShowPromotionBanner] = useState(false);
	const [timeRemaining, setTimeRemaining] = useState(calculateTimeRemaining());

	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [settings, setSettings] = useState({});
	const [initialSettings, setInitialSettings] = useState({});

	/**
	 * Alterna o estado de expansão do menu
	 */
	const toggleMenu = () => setMenuExpanded(!menuExpanded);

	/**
	 * Função para salvar as configurações do dashboard
	 */
	const handleSaveSettings = () => {
		ipcRenderer.send(channels.SAVE_SETTINGS, {
			newSettings: settings,
			instanceId,
		});
		setInitialSettings(settings);
		setHasUnsavedChanges(false);
	};

	/**
	 * Inicia a edição de uma instância
	 * @param {string} instance - Nome da instância
	 */
	const handleEditInstance = (instance) => {
		setIsEditing(instance);
		setEditedInstanceName(instance);
	};

	/**
	 * Salva o nome de uma instância após edição
	 * @param {string} oldInstanceName - Nome antigo da instância
	 */
	const handleSaveInstanceName = (oldInstanceName) => {
		if (editedInstanceName && editedInstanceName.trim() !== "") {
			if (existingInstances.includes(editedInstanceName.trim())) {
				toast.error("Já existe uma instância com esse nome!");
				return;
			}

			ipcRenderer.send(channels.RENAME_INSTANCE, {
				oldInstanceName,
				newInstanceName: editedInstanceName.trim(),
			});

			ipcRenderer.once(channels.RENAME_INSTANCE_REPLY, (_, response) => {
				if (response.success) {
					toast.success(
						`Instância renomeada para ${editedInstanceName.trim()} com sucesso!`,
					);

					if (oldInstanceName === instanceId) {
						setInstanceId(editedInstanceName.trim());
					}

					ipcRenderer.send(channels.GET_INSTANCE_LIST);
					ipcRenderer.on(channels.GET_INSTANCE_LIST_REPLY, (_, instances) => {
						setExistingInstances(instances);
					});
				} else {
					toast.error(`Erro ao renomear a instância: ${response.error}`);
				}
			});
		}
	};

	/**
	 * Exclui uma instância
	 * @param {string} instance - Nome da instância a ser excluída
	 */
	const handleDeleteInstance = (instance) => {
		if (
			window.confirm(
				`Você tem certeza que quer deletar a instância ${instance}?`,
			)
		) {
			ipcRenderer.send(channels.DELETE_INSTANCE, instance);

			ipcRenderer.once(channels.DELETE_INSTANCE_REPLY, (_, response) => {
				if (response.success) {
					toast.success(`Instância ${instance} deletada com sucesso!`);

					if (response.instanceId === instanceId) {
						setInstanceId("default");
						toast.info("Você foi movido para a instância padrão.");
					}

					ipcRenderer.send(channels.GET_INSTANCE_LIST);
					ipcRenderer.on(channels.GET_INSTANCE_LIST_REPLY, (_, instances) => {
						setExistingInstances(instances);
					});
				} else {
					toast.error(
						`Erro ao deletar a instância ${instance}: ${response.error}`,
					);
				}
			});
		}
	};

	/**
	 * Seleciona uma instância
	 * @param {string} instance - Nome da instância a ser selecionada
	 */
	const handleSelectInstance = (instance) => {
		if (instanceId !== instance) {
			setInstanceId(instance);
			setInstanceSelectionDialogOpen(false);
			toast.success(`Conta ${instance} selecionada!`);
			setInstanceName(instance === "default" ? "Padrão" : instance);
		}
	};

	/**
	 * Verifica se deve mostrar o banner de promoção
	 * @returns {boolean} true se deve mostrar o banner
	 */
	const shouldShowPromotionBanner = () => {
		const storedAccessUntil = localStorage.getItem("accessUntil");

		if (!storedAccessUntil) {
			return false;
		}

		const expirationDate = new Date(storedAccessUntil);
		const currentDate = new Date();

		// Verifica se é vitalício (mais de 20 anos no futuro)
		const twentyYearsLater = new Date();
		twentyYearsLater.setFullYear(currentDate.getFullYear() + 20);

		// Se é vitalício, NÃO mostrar banner da promoção
		if (expirationDate > twentyYearsLater) {
			return false;
		}

		// Calcula 2 meses a partir de hoje
		const twoMonthsFromNow = new Date();
		twoMonthsFromNow.setMonth(currentDate.getMonth() + 2);

		// Mostra se o acesso expira em até 2 meses ou já expirou
		// (ou seja, só para quem NÃO tem plano vitalício)
		return expirationDate <= twoMonthsFromNow;
	};

	/**
	 * Componente do banner de promoção
	 */
	const PromotionBanner = () => {
		if (!showPromotionBanner || timeRemaining.expired) {
			return null;
		}

		return (
			<div className="w-full bg-red-600 text-white py-4 px-4 relative overflow-hidden">
				{/* Efeito de fundo animado */}
				<div className="absolute inset-0 bg-gradient-to-r from-red-600 via-red-500 to-red-600 animate-pulse opacity-50" />

				{/* Conteúdo */}
				<div className="relative z-10 max-w-7xl mx-auto">
					<div className="flex items-center justify-between flex-wrap gap-3">
						<div className="flex flex-col space-y-1">
							<div className="flex items-center space-x-2">
								<Star className="w-5 h-5 text-yellow-300 animate-pulse" />
								<span className="font-bold text-lg">ANIVERSÁRIO DE 1 ANO DO ZAP GPT</span>
								<Star className="w-5 h-5 text-yellow-300 animate-pulse" />
							</div>
							<div className="text-white/90 flex items-center space-x-2">
								<span className="font-semibold">Plano Vitalício por apenas</span>
								<span className="text-3xl font-bold text-yellow-300">R$ 247,00</span>
								<span className="text-sm bg-green-500 px-2 py-1 rounded-full font-bold">+ IA ILIMITADA JÁ DISPONÍVEL!</span>
							</div>
						</div>

						{/* Contador regressivo e CTA */}
						<div className="flex items-center space-x-4">
							<div className="flex flex-col items-end space-y-1">
								<div className="flex items-center space-x-2">
									<Clock className="w-4 h-4 text-yellow-300" />
									<span className="font-semibold text-sm">Oferta acaba em:</span>
								</div>
								<div className="flex items-center space-x-1 bg-glassOverlay/20 rounded-lg px-3 py-1 font-mono text-sm">
									<div className="text-center">
										<div className="text-xl font-bold text-yellow-300">{timeRemaining.days.toString().padStart(2, '0')}</div>
										<div className="text-xs text-white/70">DIAS</div>
									</div>
									<span className="text-xl font-bold text-yellow-300 mx-1">:</span>
									<div className="text-center">
										<div className="text-xl font-bold text-yellow-300">{timeRemaining.hours.toString().padStart(2, '0')}</div>
										<div className="text-xs text-white/70">HORAS</div>
									</div>
									<span className="text-xl font-bold text-yellow-300 mx-1">:</span>
									<div className="text-center">
										<div className="text-xl font-bold text-yellow-300">{timeRemaining.minutes.toString().padStart(2, '0')}</div>
										<div className="text-xs text-white/70">MIN</div>
									</div>
									<span className="text-xl font-bold text-yellow-300 mx-1">:</span>
									<div className="text-center">
										<div className="text-xl font-bold text-yellow-300">{timeRemaining.seconds.toString().padStart(2, '0')}</div>
										<div className="text-xs text-white/70">SEG</div>
									</div>
								</div>
							</div>
							<button
								type="button"
								onClick={() => {
									shell.openExternal("https://www.ozapgpt.com.br/#pricing");
								}}
								className="bg-white text-red-600 hover:bg-yellow-100 transition-colors duration-200 font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105"
							>
								APROVEITAR AGORA
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	};

	// Atualizar o tempo de acesso e limite de instâncias quando o componente montar
	useEffect(() => {
		const storedAccessUntil = localStorage.getItem("accessUntil");
		if (storedAccessUntil) {
			const expirationDate = new Date(storedAccessUntil);
			const currentDate = new Date();
			const twentyYearsLater = new Date();
			twentyYearsLater.setFullYear(currentDate.getFullYear() + 20);

			if (expirationDate > twentyYearsLater) {
				setAccessUntil("Vitalício");
			} else {
				setAccessUntil(
					expirationDate.toLocaleDateString("pt-BR", {
						day: "2-digit",
						month: "2-digit",
						year: "numeric",
					}),
				);
			}
		}

		const storedLimit = localStorage.getItem("instancesLimit");
		if (storedLimit === "unlimited" || !storedLimit) {
			setInstancesLimit(null);
		} else {
			const limit = parseInt(storedLimit);
			if (!isNaN(limit) && limit > 0) {
				setInstancesLimit(limit);
			} else {
				setInstancesLimit(null);
			}
		}
	}, []);

	// Verificar se deve mostrar o banner ao montar o componente
	useEffect(() => {
		setShowPromotionBanner(shouldShowPromotionBanner());
	}, []);

	// Atualizar contador a cada segundo
	useEffect(() => {
		const timer = setInterval(() => {
			const newTimeRemaining = calculateTimeRemaining();
			setTimeRemaining(newTimeRemaining);

			// Se a promoção expirou, ocultar banner
			if (newTimeRemaining.expired) {
				setShowPromotionBanner(false);
			}
		}, 1000);

		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		ipcRenderer.on(channels.STATUS_UPDATE, (_, arg) => {
			const { status, instanceId: incomingInstanceId } = arg;
			const isConnected = status === "open";
			const isCloseByUser = status === "close-by-user";

			setInstances((prevInstances) => {
				const updatedInstance = {
					...prevInstances[incomingInstanceId],
					connected: isConnected,
				};

				if (isConnected) {
					updatedInstance.qrCode = null;
				}

				if (isCloseByUser) {
					updatedInstance.qrCode = null;
					toast.warning(
						"Conexão encerrada pelo usuário. Escaneie o QR Code novamente.",
					);
				}

				return {
					...prevInstances,
					[incomingInstanceId]: updatedInstance,
				};
			});
		});

		ipcRenderer.send(channels.SET_INSTANCE_ID, instanceId);

		ipcRenderer.send(channels.GET_INSTANCE_LIST);
		ipcRenderer.on(channels.GET_INSTANCE_LIST_REPLY, (_, instances) => {
			setExistingInstances(instances);

			instances.forEach((instance) => {

				ipcRenderer
					.invoke("get-connection-status", instance)
					.then((status) => {
						setInstances((prevInstances) => ({
							...prevInstances,
							[instance]: {
								...prevInstances[instance],
								connected: status.connected,
							},
						}));
					})
					.catch((error) => {
						console.error(
							`Erro ao verificar status da instância ${instance}:`,
							error,
						);
					});
			});
		});

		return () => {
			ipcRenderer.removeAllListeners(channels.STATUS_UPDATE);
			ipcRenderer.removeAllListeners(channels.GET_INSTANCE_LIST_REPLY);
		};
	}, [instanceId]);

	/**
	 * Função para copiar logs para a área de transferência
	 */
	const handleCopyLogs = () => {
		ipcRenderer.send("copy-logs", logs.join("\n"));
		toast.success("Logs copiados para a área de transferência!");
	};

	// Carregar logs quando o modal de logs estiver aberto
	useEffect(() => {
		let intervalId;

		if (logsDialogOpen) {
			// Buscar logs iniciais
			ipcRenderer.invoke("get-logs").then((newLogs) => {
				setLogs(newLogs);
			});

			// Atualizar logs a cada segundo
			intervalId = setInterval(async () => {
				const newLogs = await ipcRenderer.invoke("get-logs");
				setLogs(newLogs);
			}, 1000);
		}

		return () => {
			if (intervalId) clearInterval(intervalId);
		};
	}, [logsDialogOpen]);

	// Validação global de autenticação a cada 5 minutos
	useEffect(() => {
		fetchOzapAiConfig(); // initial fetch on mount
		const interval = setInterval(() => {
			validateAuthToken();
			fetchOzapAiConfig();
		}, 300000); // 300.000 milissegundos = 5 minutos

		return () => clearInterval(interval);
	}, []);

	return (
		<div className="flex flex-col h-screen bg-dashboardBg">
			{/* Conteúdo principal */}
			<div className="flex-1 overflow-auto relative pb-24 main-scrollbar">
				<AIErrorNotification instanceId={instanceId} onToastClick={() => setCurrentPage("dashboard")} />
				{currentPage === "dashboard" && (
					<Dashboard
						onLogout={onLogout}
						setInstanceSelectionDialogOpen={setInstanceSelectionDialogOpen}
						setLogsDialogOpen={setLogsDialogOpen}
						setUpdatesDialogOpen={setUpdatesDialogOpen}
						instanceSelectionDialogOpen={false}
						logsDialogOpen={false}
						updatesDialogOpen={false}
						setInstanceName={setInstanceName}
						setAccessUntil={setAccessUntil}
						instanceId={instanceId}
						setInstanceId={setInstanceId}
						hasUnsavedChanges={hasUnsavedChanges}
						setHasUnsavedChanges={setHasUnsavedChanges}
						settings={settings}
						setSettings={setSettings}
						initialSettings={initialSettings}
						setInitialSettings={setInitialSettings}
						handleSaveSettings={handleSaveSettings}
					/>
				)}
				{currentPage === "dashboardV2" && <DashboardV2 />}
				{currentPage === "chat" && <Chat instanceId={instanceId} />}
				{currentPage === "tutorial" && <Tutorial />}
			</div>

			<UpdatesDialog
				isOpen={updatesDialogOpen}
				onClose={() => setUpdatesDialogOpen(false)}
			/>
			<LogsDialog
				isOpen={logsDialogOpen}
				onClose={() => setLogsDialogOpen(false)}
				logs={logs}
				handleCopyLogs={handleCopyLogs}
			/>

			<Dialog
				open={options247DialogOpen}
				onOpenChange={setOptions247DialogOpen}
			>
				<DialogContent className="max-w-4xl rounded-lg border border-primaryColor/20 bg-gradient-to-br from-dashboardBg to-dashboardCard p-6">
					<DialogHeader className="border-b border-primaryColor/10 pb-4">
						<DialogTitle className="text-xl font-bold text-primaryColor">
							Opções para Execução 24/7
						</DialogTitle>
					</DialogHeader>

					<div className="mt-4 overflow-auto">
						<table className="w-full border-collapse">
							<thead>
								<tr className="border-b border-primaryColor/20">
									<th className="p-3 text-left text-primaryColor">Opção</th>
									<th className="p-3 text-left text-primaryColor">Como funciona</th>
									<th className="p-3 text-left text-primaryColor">Custos</th>
								</tr>
							</thead>
							<tbody>
								<tr className="border-b border-dashboardCard/30">
									<td className="p-3 text-white">💻 App Desktop (local)</td>
									<td className="p-3 text-gray-300">Responde enquanto o app estiver aberto no seu PC.</td>
									<td className="p-3 text-gray-300">Nenhum custo adicional com instancias ilimitadas.</td>
								</tr>
								<tr className="border-b border-dashboardCard/30">
									<td className="p-3 text-white">🖥️ VPS Windows (sua própria)</td>
									<td className="p-3 text-gray-300">Você instala o {APP_NAME} numa VPS Windows e deixa as janelas abertas lá.</td>
									<td className="p-3 text-gray-300">Você paga apenas a VPS (5$ / mês). Instâncias ilimitadas. Tutorial passo‑a‑passo incluso na aba de "Tutoriais".</td>
								</tr>
								<tr>
									<td className="p-3 text-white">☁️ {APP_NAME} Cloud (em breve)</td>
									<td className="p-3 text-gray-300">Rodamos tudo nos nossos servidores, 24/7, sem precisar configurar nada.</td>
									<td className="p-3 text-gray-300">Cobrança por instância ativa para cobrir a infraestrutura.</td>
								</tr>
							</tbody>
						</table>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog
				open={instanceSelectionDialogOpen}
				onOpenChange={setInstanceSelectionDialogOpen}
			>
				<DialogContent className="max-w-2xl rounded-lg border border-primaryColor/20 bg-gradient-to-br from-dashboardBg to-dashboardCard p-6 max-h-[80vh] flex flex-col">
					<DialogHeader className="border-b border-primaryColor/10 pb-4">
						<DialogTitle className="text-xl font-bold text-primaryColor">
							Configuração de Múltiplas Instâncias
						</DialogTitle>
					</DialogHeader>

					<div
						className="overflow-y-auto flex-1 pr-2"
						style={{ maxHeight: "60vh" }}
					>
						<div className="rounded-lg border border-primaryColor/10 bg-dashboardCard/50 p-6 shadow-lg">
							<p className="text-base text-gray-300">
								Gerencie múltiplos números de WhatsApp facilmente. Selecione uma
								conta para alternar ou configure uma nova sem precisar abrir
								várias instâncias.
							</p>
							{instancesLimit && (
								<div className="mt-3 p-3 bg-warningBg border border-warningBorder rounded-lg">
									<p className="text-sm text-statusYellowLight">
										⚠️ Seu plano permite até <strong>{instancesLimit}</strong> {instancesLimit === 1 ? 'instância' : 'instâncias'}.
										{existingInstances.length > instancesLimit && (
											<span className="block mt-1">
												Você tem {existingInstances.length} configuradas, mas só poderá usar as primeiras {instancesLimit}.
											</span>
										)}
									</p>
								</div>
							)}
						</div>

						<h3 className="my-4 text-xl font-semibold text-primaryColor">
							Instâncias Configuradas:
						</h3>

													<div className="mb-6 max-h-60 overflow-y-auto">
							<ul className="space-y-4">
								{existingInstances.map((instance, index) => {
									const isDisabled = instancesLimit && index >= instancesLimit;
									return (
									<li
										key={instance}
										className={`flex items-center justify-between space-x-4 ${
											isDisabled ? 'opacity-40 pointer-events-none' : ''
										}`}
									>
										<div
											className={`grow rounded-lg p-4 shadow transition-all duration-200 ${
												isDisabled
													? "cursor-not-allowed bg-statusErrorDark/20 border border-statusError/20"
													: instanceId === instance
													? "cursor-not-allowed bg-dashboardBg/70"
													: "cursor-pointer bg-dashboardCard hover:bg-cardHover"
											}`}
											onClick={() => {
												if (!isDisabled && instanceId !== instance) {
													handleSelectInstance(instance);
												}
											}}
											onKeyDown={(e) => {
												if (
													(e.key === "Enter" || e.key === " ") &&
													instanceId !== instance
												) {
													handleSelectInstance(instance);
												}
											}}
											style={{ opacity: instanceId === instance ? 0.5 : 1 }}
										>
											<div className="flex items-center space-x-3">
												{isEditing === instance ? (
													<input
														type="text"
														value={editedInstanceName}
														onClick={(e) => e.stopPropagation()}
														onChange={(e) =>
															setEditedInstanceName(e.target.value)
														}
														className="w-20 rounded-lg border border-primaryColor/30 bg-dashboardBg px-2 py-1 text-white"
													/>
												) : (
													<span className="text-lg font-semibold text-white">
														{instance === "default" ? "Padrão" : instance}
														{isDisabled && (
															<span className="ml-2 text-xs text-statusError">
																(Excede o limite)
															</span>
														)}
													</span>
												)}

												<span
													className={`flex items-center ${instances[instance]?.connected ? "text-statusSuccess" : "text-statusError"}`}
												>
													{instances[instance]?.connected ? (
														<CheckCircleIcon className="size-6" />
													) : (
														<XCircleIcon className="size-6" />
													)}
													<span className="ml-1">
														{instances[instance]?.connected
															? "Conectado"
															: "Desconectado"}
													</span>
												</span>
											</div>
										</div>

										<div className="flex space-x-2">
											{instance !== "default" && (
												<>
													{isEditing === instance ? (
														<>
															<button
																type="button"
																onClick={(e) => {
																	e.stopPropagation();
																	handleSaveInstanceName(instance);
																}}
																className="rounded-lg bg-gradient-to-r from-primaryColor to-dashboardAccent px-3 py-1 text-white hover:shadow-lg hover:from-dashboardAccent hover:to-primaryColor"
															>
																Salvar
															</button>
															<button
																onClick={(e) => {
																	e.stopPropagation();
																	setIsEditing(null);
																}}
																type="button"
																className="rounded-lg border border-statusError/50 bg-transparent px-3 py-1 text-statusError hover:bg-statusError/20"
															>
																Cancelar
															</button>
														</>
													) : (
														<button
															onClick={(e) => {
																e.stopPropagation();
																handleEditInstance(instance);
															}}
															type="button"
															className="rounded-lg border border-primaryColor/30 bg-transparent p-2 text-primaryColor hover:bg-primaryColor/10"
														>
															<EditIcon className="size-5" />
														</button>
													)}
												</>
											)}

											{instance !== "default" && (
												<button
													onClick={(e) => {
														e.stopPropagation();
														handleDeleteInstance(instance);
													}}
													type="button"
													className="rounded-lg border border-statusError/50 bg-transparent p-2 text-statusError hover:bg-statusError/10"
												>
													<TrashIcon className="size-5" />
												</button>
											)}
										</div>
									</li>
									);
								})}
							</ul>
						</div>
					</div>

					<div className="border-t border-primaryColor/10 pt-4">
						{instancesLimit && existingInstances.length >= instancesLimit ? (
							<div className="p-4 bg-statusErrorDark/20 border border-statusError/30 rounded-lg">
								<p className="text-statusErrorLight text-center">
									❌ Você atingiu o limite de {instancesLimit} {instancesLimit === 1 ? 'instância' : 'instâncias'} do seu plano.
								</p>
								<p className="text-statusError text-center text-sm mt-2">
									Para adicionar mais instâncias, faça upgrade do seu plano.
								</p>
							</div>
						) : (
							<div className="flex justify-between items-center">
								<input
									type="text"
									value={newInstanceName}
									onChange={(e) => setNewInstanceName(e.target.value)}
									className="w-full rounded-lg border border-primaryColor/30 bg-dashboardBg px-4 py-2 text-white focus:border-primaryColor focus:ring focus:ring-primaryColor/30"
									placeholder="Nome para a nova configuração"
								/>
								<button
									className="ml-4 rounded-lg bg-gradient-to-r from-primaryColor to-dashboardAccent px-6 py-2 font-medium text-white transition hover:shadow-lg hover:from-dashboardAccent hover:to-primaryColor"
									onClick={() => {
										if (newInstanceName.trim() !== "") {
											setInstanceId(newInstanceName.trim());
											setInstanceSelectionDialogOpen(false);
											setNewInstanceName("");
										}
									}}
									type="button"
								>
									Criar Nova Conta
								</button>
							</div>
						)}
					</div>
				</DialogContent>
			</Dialog>

			{menuExpanded && (
				<>
					<div
						className="fixed inset-0 bg-glassOverlay/50 backdrop-blur-sm transition-opacity duration-300"
						onClick={toggleMenu}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								toggleMenu();
							}
						}}
						role="button"
						tabIndex={0}
						aria-label="Fechar menu"
					/>

					<div className="absolute bottom-16 left-0 right-0 w-full bg-menuBg backdrop-blur-md shadow-2xl border-t border-menuBorder overflow-hidden transition-all duration-300 animate-slideUp z-10">
						<div className="py-5 px-4 mx-auto">
							<div className="grid grid-cols-3 gap-3 mx-auto mb-4">
								<button
									type="button"
									onClick={() => setLogsDialogOpen(true)}
									className="relative rounded-lg px-3 py-2.5 text-sm font-medium text-white overflow-hidden shadow-md cursor-pointer group whitespace-nowrap"
								>
									<span className="relative z-10 flex items-center justify-center">
										<MessageSquare size={17} className="mr-1.5 text-navText" />
										Ver Logs
									</span>
									<div className="absolute inset-0 bg-gradient-to-r from-menuGradientStart to-menuGradientEnd transition-colors duration-200" />
									<div className="absolute inset-0 bg-gradient-to-r from-menuHoverStart to-menuHoverEnd opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
								</button>

								<button
									type="button"
									onClick={() => setUpdatesDialogOpen(true)}
									className="relative rounded-lg px-3 py-2.5 text-sm font-medium text-white overflow-hidden shadow-md cursor-pointer group whitespace-nowrap bg-gradient-to-r from-primaryColor to-dashboardAccent"
								>
									<span className="relative z-10 flex items-center justify-center">
										<Info size={17} className="mr-1.5 text-white" />
										Versão: {version}
									</span>
								</button>

								<button
									onClick={onLogout}
									className="relative rounded-lg px-3 py-2.5 text-sm font-medium text-white overflow-hidden shadow-md cursor-pointer group whitespace-nowrap"
									type="button"
								>
									<span className="relative z-10 flex items-center justify-center">
										<LogOut size={17} className="mr-1.5" />
										Sair da Conta
									</span>
									<div className="absolute inset-0 bg-gradient-to-r from-redDark to-redDarker transition-colors duration-200" />
									<div className="absolute inset-0 bg-gradient-to-r from-redLight to-redDark opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
								</button>
							</div>

							<div className="flex justify-center mb-4">
								<button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										shell.openExternal(SUPPORT_WHATSAPP);
									}}
									className="relative rounded-lg px-4 py-2.5 text-sm font-medium text-yellow-900 w-full max-w-xs bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 hover:shadow-lg hover:shadow-yellow-400/25 transition-shadow duration-200"
								>
									<span className="relative z-10 flex items-center justify-center">
										<MessageSquare size={17} className="mr-1.5 text-yellow-900" />
										Suporte via WhatsApp(NOVO)
									</span>
								</button>
							</div>

							<div className="text-center my-4">
								<div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-sm">
									<p className="text-amber-200 mb-2">
										⚠️ {APP_NAME} funciona só enquanto o app estiver aberto.
									</p>
									<button
										type="button"
										onClick={() => setOptions247DialogOpen(true)}
										className="text-blue-400 hover:text-blue-300 transition-colors underline bg-transparent"
									>
										Precisa rodar 24/7? ➜ Clique aqui para ver as opções ☁️🖥️
									</button>
								</div>
							</div>

							<div className="text-center text-gray-400 bg-accessBg/50 px-5 py-3 rounded-full mx-auto max-w-xs text-sm">
								Acesso válido até:{" "}
								<strong className="text-accessValidText">{accessUntil}</strong>
							</div>
						</div>
					</div>
				</>
			)}

			<div className="fixed bottom-0 left-0 right-0 z-40 w-full bg-glassBg/5 backdrop-blur-xl border-t border-glassBg/10">
				<div className="max-w-2xl mx-auto px-4 py-3">
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-2">
							<button
								type="button"
								onClick={() => setCurrentPage("dashboard")}
								className={`group relative flex flex-col items-center justify-center px-4 py-2 rounded-xl transition-all duration-200 ${
									currentPage === "dashboard"
										? "bg-gradient-to-r from-primaryColor to-dashboardAccent text-white shadow-lg shadow-primaryColor/30"
										: "text-textMuted hover:text-white hover:bg-glassBg/10"
								}`}
							>
								<LayoutDashboard className="h-5 w-5 mb-1" />
								<span className="text-xs font-medium">Central</span>
							</button>

							<button
								type="button"
								onClick={() => setCurrentPage("dashboardV2")}
								className={`group relative flex flex-col items-center justify-center px-4 py-2 rounded-xl transition-all duration-200 ${
									currentPage === "dashboardV2"
										? "bg-gradient-to-r from-primaryColor to-dashboardAccent text-white shadow-lg shadow-primaryColor/30"
										: "text-textMuted hover:text-white hover:bg-glassBg/10"
								}`}
							>
								<LayoutPanelTop className="h-5 w-5 mb-1" />
								<span className="text-xs font-medium">Métricas</span>
							</button>

							<button
								type="button"
								onClick={() => setCurrentPage("chat")}
								className={`group relative flex flex-col items-center justify-center px-4 py-2 rounded-xl transition-all duration-200 ${
									currentPage === "chat"
										? "bg-gradient-to-r from-primaryColor to-dashboardAccent text-white shadow-lg shadow-primaryColor/30"
										: "text-textMuted hover:text-white hover:bg-glassBg/10"
								}`}
							>
								<MessageSquare className="h-5 w-5 mb-1" />
								<span className="text-xs font-medium">Chat</span>
							</button>
						</div>

						<div className="flex-1 flex items-center justify-center px-8">
							<div className="flex items-center justify-center min-w-[100px] h-10">
								{currentPage === "dashboard" ? (
									<button
										type="button"
										onClick={handleSaveSettings}
										className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
											hasUnsavedChanges
												? "bg-gradient-to-r from-primaryColor to-dashboardAccent text-white hover:scale-[1.02] hover:shadow-lg hover:shadow-primaryColor/30 cursor-pointer shadow-lg shadow-primaryColor/25"
												: "cursor-not-allowed bg-whiteBase/10 text-whiteBase/60 border border-whiteBase/20"
										}`}
										disabled={!hasUnsavedChanges}
									>
										<CheckCircle className="h-4 w-4" />
										<span className="text-sm">Salvar</span>
									</button>
								) : (
									<div className="relative group">
										<img
											src={Logo}
											alt={APP_LOGO_ALT}
											className="h-8 w-auto transition-transform duration-200 hover:scale-110 filter drop-shadow-lg"
										/>
									</div>
								)}
							</div>
						</div>

						<div className="flex items-center space-x-2">
							<button
								type="button"
								onClick={() => setInstanceSelectionDialogOpen(true)}
								className="group relative flex flex-col items-center justify-center px-3 py-2 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 hover:from-blue-500/30 hover:to-purple-500/30 hover:text-blue-200 transition-all duration-200 border border-blue-400/30"
							>
								<User className="h-5 w-5 mb-1" />
								<div className="flex items-center space-x-1">
									<span className="text-xs font-medium max-w-[60px] truncate">
										{instanceName}
									</span>
									<ChevronUp className="h-3 w-3 rotate-180" />
								</div>
							</button>

							<button
								type="button"
								onClick={() => setCurrentPage("tutorial")}
								className={`group relative flex flex-col items-center justify-center px-4 py-2 rounded-xl transition-all duration-200 ${
									currentPage === "tutorial"
										? "bg-gradient-to-r from-primaryColor to-dashboardAccent text-white shadow-lg shadow-primaryColor/30"
										: "text-textMuted hover:text-white hover:bg-glassBg/10"
								}`}
							>
								<PlayCircle className="h-5 w-5 mb-1" />
								<span className="text-xs font-medium">Tutorial</span>
							</button>

							<button
								type="button"
								onClick={toggleMenu}
								className={`group relative flex flex-col items-center justify-center px-4 py-2 rounded-xl transition-all duration-200 ${
									menuExpanded
										? "bg-gradient-to-r from-primaryColor to-dashboardAccent text-white shadow-lg shadow-primaryColor/30"
										: "text-textMuted hover:text-white hover:bg-glassBg/10"
								}`}
							>
								{menuExpanded ? (
									<X className="h-5 w-5 mb-1 transition-transform duration-200 rotate-90" />
								) : (
									<Settings className="h-5 w-5 mb-1 transition-transform duration-200 group-hover:rotate-45" />
								)}
								<span className="text-xs font-medium">Config</span>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default AppLayout;
